import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/StudentChat.css";
import MentiBot from "./images/menti.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faBookOpen, faFolderOpen, faBookAtlas, faBullseye, faMagic, faHistory, faCog } from '@fortawesome/free-solid-svg-icons';

interface Class {
  id: number;
  name: string;
}

interface Item {
  id: number;
  name: string;
}

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000";

function StudentChat() {
  const navigate = useNavigate();

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedSeries, setSelectedSeries] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");

  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Item[]>([]);
  const [series, setSeries] = useState<Item[]>([]);
  const [chapters, setChapters] = useState<Item[]>([]);
  const [topics, setTopics] = useState<Item[]>([]);

  const [loading, setLoading] = useState({
    classes: false,
    subjects: false,
    series: false,
    chapters: false,
    topics: false,
  });

  useEffect(() => {
    setLoading((prev) => ({ ...prev, classes: true }));

    fetch(`${API_BASE_URL}/api/classes`)
      .then((res) => res.json())
      .then((data) => setClasses(data.data || []))
      .catch((err) => console.error(err))
      .finally(() =>
        setLoading((prev) => ({ ...prev, classes: false }))
      );
  }, []);

  useEffect(() => {
    if (!selectedClass) {
      setSubjects([]);
      return;
    }

    setLoading((prev) => ({ ...prev, subjects: true }));

    fetch(
      `${API_BASE_URL}/api/subjects?class=${encodeURIComponent(
        selectedClass
      )}`
    )
      .then((res) => res.json())
      .then((data) => setSubjects(data.data || []))
      .catch(console.error)
      .finally(() =>
        setLoading((prev) => ({ ...prev, subjects: false }))
      );
  }, [selectedClass]);

  useEffect(() => {
    if (!selectedSubject) {
      setSeries([]);
      return;
    }

    setLoading((prev) => ({ ...prev, series: true }));

    fetch(
      `${API_BASE_URL}/api/series?class_id=${selectedClass}&subject_id=${selectedSubject}`
    )
      .then((res) => res.json())
      .then((data) => setSeries(data.data || []))
      .catch(console.error)
      .finally(() =>
        setLoading((prev) => ({ ...prev, series: false }))
      );
  }, [selectedClass, selectedSubject]);

  useEffect(() => {
    if (!selectedSeries) {
      setChapters([]);
      return;
    }

    setLoading((prev) => ({ ...prev, chapters: true }));

    fetch(
      `${API_BASE_URL}/api/chapters?class_id=${selectedClass}&subject_id=${selectedSubject}&series_id=${selectedSeries}`
    )
      .then((res) => res.json())
      .then((data) => setChapters(data.data || []))
      .catch(console.error)
      .finally(() =>
        setLoading((prev) => ({ ...prev, chapters: false }))
      );
  }, [selectedClass, selectedSubject, selectedSeries]);

  useEffect(() => {
    if (!selectedChapter) {
      setTopics([]);
      return;
    }

    setLoading((prev) => ({ ...prev, topics: true }));

    fetch(
      `${API_BASE_URL}/api/topics?class_id=${selectedClass}&subject_id=${selectedSubject}&series_id=${selectedSeries}&chapter_id=${selectedChapter}`
    )
      .then((res) => res.json())
      .then((data) => setTopics(data.data || []))
      .catch(console.error)
      .finally(() =>
        setLoading((prev) => ({ ...prev, topics: false }))
      );
  }, [
    selectedClass,
    selectedSubject,
    selectedSeries,
    selectedChapter,
  ]);

  const find = (list: any[], id: string) =>
    list.find((item) => String(item.id) === id);

  const handleStartChat = () => {
    navigate("/chat", {
      state: {
        classId: Number(selectedClass),
        subjectId: Number(selectedSubject),
        seriesId: Number(selectedSeries),
        chapterId: Number(selectedChapter),
        topicId: selectedTopic ? Number(selectedTopic) : null,

        className: find(classes, selectedClass)?.name,
        subjectName: find(subjects, selectedSubject)?.name,
        seriesName: find(series, selectedSeries)?.name,
        chapterName: find(chapters, selectedChapter)?.name,
        topicName: selectedTopic
          ? find(topics, selectedTopic)?.name
          : null,
      },
    });
  };

  return (
    <div className="student-page">
      <div className="bg-circle bg-circle-1"></div>
      <div className="bg-circle bg-circle-2"></div>
      <div className="bg-circle bg-circle-3"></div>
      <div className="student-card">
        <div className="logo-wrapper">
          <div className="logo-circle">
            <img src={MentiBot} alt="Menti AI" width="150"/>
          </div>
          <h2>Hi! I'm <span className="linear-wipe">Menti</span></h2>
          <p>
           Your AI learning companion. Let's learn together!
          </p>

        </div>

        <div className="progress-wrapper">

          <div className={selectedClass ? "step active" : "step"}>1</div>

          <div className={selectedSubject ? "line active" : "line"}></div>

          <div className={selectedSubject ? "step active" : "step"}>2</div>

          <div className={selectedSeries ? "line active" : "line"}></div>

          <div className={selectedSeries ? "step active" : "step"}>3</div>

          <div className={selectedChapter ? "line active" : "line"}></div>

          <div className={selectedChapter ? "step active" : "step"}>4</div>

        </div>

        <div className="modern-form">

          <div className="form-group-modern">

            <label>
              <FontAwesomeIcon icon={faBook} /> Select Class
            </label>

            <select
              className="modern-select"
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedSubject("");
                setSelectedSeries("");
                setSelectedChapter("");
                setSelectedTopic("");
              }}
              disabled={loading.classes}
            >

              <option value="">
                {loading.classes
                  ? "Loading..."
                  : "Choose Class"}
              </option>

              {classes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}

            </select>

          </div>

          <div className="form-group-modern">

            <label>
              <FontAwesomeIcon icon={faBookOpen} /> Select Subject
            </label>

            <select
              className="modern-select"
              value={selectedSubject}
              disabled={!selectedClass || loading.subjects}
              onChange={(e) => {
                setSelectedSubject(e.target.value);
                setSelectedSeries("");
                setSelectedChapter("");
                setSelectedTopic("");
              }}
            >
              <option value="">
                {loading.subjects ? "Loading..." : "Choose Subject"}
              </option>

              {subjects.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

          </div>

          <div className="form-group-modern">

            <label>
              <FontAwesomeIcon icon={faFolderOpen} /> Select Series
            </label>

            <select
              className="modern-select"
              value={selectedSeries}
              disabled={!selectedSubject || loading.series}
              onChange={(e) => {
                setSelectedSeries(e.target.value);
                setSelectedChapter("");
                setSelectedTopic("");
              }}
            >
              <option value="">
                {loading.series ? "Loading..." : "Choose Series"}
              </option>

              {series.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

          </div>

          <div className="form-group-modern">

            <label>
              <FontAwesomeIcon icon={faBookAtlas} /> Select Chapter
            </label>

            <select
              className="modern-select"
              value={selectedChapter}
              disabled={!selectedSeries || loading.chapters}
              onChange={(e) => {
                setSelectedChapter(e.target.value);
                setSelectedTopic("");
              }}
            >
              <option value="">
                {loading.chapters ? "Loading..." : "Choose Chapter"}
              </option>

              {chapters.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

          </div>

          <div className="form-group-modern">

            <label>
              <FontAwesomeIcon icon={faBullseye} /> Select Topic (Optional)
            </label>

            <select
              className="modern-select"
              value={selectedTopic}
              disabled={!selectedChapter || loading.topics}
              onChange={(e) => setSelectedTopic(e.target.value)}
            >
              <option value="">
                {loading.topics ? "Loading..." : "All Topics"}
              </option>

              {topics.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

          </div>

        </div>

        <div className="selection-summary">

          <h6>Your Learning Path</h6>

          <div className="summary-text">

            {selectedClass
              ? `${find(classes, selectedClass)?.name || ""}`
              : "Class"}

            <span>›</span>

            {selectedSubject
              ? `${find(subjects, selectedSubject)?.name || ""}`
              : "Subject"}

            <span>›</span>

            {selectedSeries
              ? `${find(series, selectedSeries)?.name || ""}`
              : "Series"}

            <span>›</span>

            {selectedChapter
              ? `${find(chapters, selectedChapter)?.name || ""}`
              : "Chapter"}

            {selectedTopic && (
              <>
                <span>›</span>

                {find(topics, selectedTopic)?.name}
              </>
            )}

          </div>

        </div>

        <button
          className="start-chat-btn"
          disabled={!selectedChapter}
          onClick={handleStartChat}
        >
          <FontAwesomeIcon icon={faMagic} /> Start AI Chat
        </button>

        <div className="bottom-actions">

          <button
            className="secondary-btn d-none"
            onClick={() => alert("History")}
          >
            <FontAwesomeIcon icon={faHistory} /> History
          </button>

          <button
            className="secondary-btn d-none"
            onClick={() => alert("Settings")}
          >
            <FontAwesomeIcon icon={faCog} /> Settings
          </button>

        </div>

      </div>

    </div>
  );
}

export default StudentChat;
